import {
    BannerMessage,
    BasicTenantSettingsResponse,
    ConsumerTenantFeatureSwitches,
    PortalSafeDataCollectionLimitResponse,
    TenantUserMetadataSetting,
} from '../../Models/Api/strongbox.financialportal';
import {
    GetBasicTenantSettings as GetBasicTenantSettingsService,
    GetTenantBannerMessages,
    GetTenantDataCollectionLimit,
    GetTenantEmailDomains,
    GetTenantFeatureSwitches as GetTenantFeatureSwitchesService,
    GetTenantUserMetadataSettings,
} from '../../Services/Tenant';
import { LogException } from '../../Utils/Logging';
import {
    AppThunkActionAsync
} from '../index';
import { KnownAction as uiKnownActions, actionCreators as uiStateActions } from '../UIState';
import {
    IOverrideActiveTenant,
    ISetBasicTenantSettings,
    ISetBrandConfig,
    KnownAction,
    TenantActions,
    TenantBasicSettings,
} from './Actions';
import {
    BrandConfig,
} from './Reducer';

function SetTenantOverride(tenant: string): IOverrideActiveTenant {
    return {
        type: TenantActions.TenantOverrideActiveTenant,
        tenant,
    };
}

function SetBasicTenantSettings(settings: TenantBasicSettings): ISetBasicTenantSettings {
    return {
        type: TenantActions.TenantSetBasicSettings,
        settings,
    };
}

function SetBrandConfig(brandconfig: BrandConfig): ISetBrandConfig {
    return {
        type: TenantActions.TenantSetBrandConfig,
        brandconfig,
    };
}

const GetEmailDomains = (): AppThunkActionAsync<string[], KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<string[]> => {
        const workingName = `FetchingEmailDomains`;

        dispatch(uiStateActions.SetPortalWorking(workingName));

        try {
            dispatch({ type: TenantActions.TenantListEmailDomains })

            const domains = await GetTenantEmailDomains();

            dispatch({
                type: TenantActions.TenantListEmailDomainsComplete,
                domains,
            });

            return domains;
        } catch (e) {
            const errorMsg = 'Listing email domains action creator failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantListEmailDomainsComplete,
                domains: [],
            })

            return [];
        } finally {
            dispatch(uiStateActions.SetPortalIdle(workingName));
        }
    }


const GetUserMetadataSettings = (): AppThunkActionAsync<TenantUserMetadataSetting[], KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<TenantUserMetadataSetting[]> => {
        const workingName = `FetchingUserMetadataSettings`;

        dispatch(uiStateActions.SetPortalWorking(workingName));

        try {
            dispatch({ type: TenantActions.TenantListUserMetadataSettings })

            const metadataSettings = await GetTenantUserMetadataSettings();

            dispatch({
                type: TenantActions.TenantListUserMetadataSettingsComplete,
                metadataSettings,
            });

            return metadataSettings;
        } catch (e) {
            const errorMsg = 'Listing user metadata settings action creator failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantListUserMetadataSettingsComplete,
                metadataSettings: [],
            })

            return [];
        } finally {
            dispatch(uiStateActions.SetPortalIdle(workingName));
        }
    }

const FetchBannerMessages = (): AppThunkActionAsync<BannerMessage[], KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<BannerMessage[]> => {
        const workingName = `FetchingBannerMessages`;

        dispatch(uiStateActions.SetPortalWorking(workingName));

        try {
            dispatch({ type: TenantActions.TenantGetBannerMessages })

            const bannerMessages = await GetTenantBannerMessages();

            dispatch({
                type: TenantActions.TenantGetBannerMessagesComplete,
                messages: bannerMessages,
            });

            return bannerMessages;
        } catch (e) {
            const errorMsg = 'Retrieving portal banner messages action creator failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantGetBannerMessagesComplete,
                messages: [],
            })

            return [];
        } finally {
            dispatch(uiStateActions.SetPortalIdle(workingName));
        }
    }

/// if setWorkingState is true, the working state will be changed while this operation is executing. 
/// The summary window at the top of the main screen behaves differently depending on the working state.
/// Because this method is called on a refresh period, that will cause a re-render of the entire screen.
/// Since the rendering state is irrelevant on all but the very first call here, the setWorkingState value
/// is generally false to prevent the annoying "screen flash" caused by the re-render.

const GetDataCollectionLimit = (setWorkingState: boolean): AppThunkActionAsync<PortalSafeDataCollectionLimitResponse | undefined, KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<PortalSafeDataCollectionLimitResponse | undefined> => {
        const workingName = `GetDataCollectionLimit`;

        if (setWorkingState) {
            dispatch(uiStateActions.SetPortalWorking(workingName));
        }

        try {
            dispatch({ type: TenantActions.TenantGetDataCollectionLimit })

            const limits = await GetTenantDataCollectionLimit();

            dispatch({
                type: TenantActions.TenantGetDataCollectionLimitComplete,
                limits,
            });

            return limits;
        } catch (e) {
            const errorMsg = 'Retrieving tenant data collection limits failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantGetDataCollectionLimitComplete,
            });

            return undefined;
        } finally {
            if (setWorkingState) {
                dispatch(uiStateActions.SetPortalIdle(workingName));
            }
        }
    }

const GetBasicTenantSettings = (): AppThunkActionAsync<BasicTenantSettingsResponse | undefined, KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<BasicTenantSettingsResponse | undefined> => {
        const workingName = `GettingBasicSettings`;

        dispatch(uiStateActions.SetPortalWorking(workingName));

        try {
            dispatch({ type: TenantActions.TenantGetBasicSettings })

            const tenantSettings = await GetBasicTenantSettingsService();

            dispatch({
                type: TenantActions.TenantGetBasicSettingsComplete,
                settings: tenantSettings,
            });

            return tenantSettings;
        } catch (e) {
            const errorMsg = 'Retrieving basic tenant settings action creator failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantGetBasicSettingsComplete,
                settings: undefined,
            })

            return undefined;
        } finally {
            dispatch(uiStateActions.SetPortalIdle(workingName));
        }
    }

const FetchTenantFeatureSwitches = (): AppThunkActionAsync<ConsumerTenantFeatureSwitches | undefined, KnownAction | uiKnownActions> =>
    async (dispatch, getState): Promise<ConsumerTenantFeatureSwitches | undefined> => {
        const workingName = `FetchingTenantFeatureSwitches`;

        dispatch(uiStateActions.SetPortalWorking(workingName));

        try {
            dispatch({ type: TenantActions.TenantGetFeatureSwitches })

            const featureSwitches = await GetTenantFeatureSwitchesService();

            dispatch({
                type: TenantActions.TenantGetFeatureSwitchesComplete,
                featureSwitches,
            });

            return featureSwitches;
        } catch (e) {
            const errorMsg = 'Retrieving tenant feature switches action creator failed';

            console.error(errorMsg);
            console.error(e);

            LogException(errorMsg, e);

            dispatch({
                type: TenantActions.TenantGetFeatureSwitchesComplete,
                featureSwitches: undefined,
            })

            return undefined;
        } finally {
            dispatch(uiStateActions.SetPortalIdle(workingName));
        }
    }

export const TenantActionCreators = {
    FetchBannerMessages,
    GetDataCollectionLimit,
    GetEmailDomains,
    GetUserMetadataSettings,
    SetTenantOverride,
    SetBasicTenantSettings,
    SetBrandConfig,
    GetBasicTenantSettings,
    FetchTenantFeatureSwitches,
};
